/*
 * Copyright 2025 ixPilot Teknoloji A.Ş.
 * All rights reserved.
 */

import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Box,
  VStack,
  HStack,
  Text,
  Button,
  useToast,
  Spinner,
  Icon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
} from "@chakra-ui/react";
import { FiRefreshCw, FiClock, FiUser } from "react-icons/fi";
import api from "utils/api";
import { formatDate } from "utils/dateFormatter";

// Plan değerini formatla (örn: "12" -> "12 Ay")
const formatPlan = (tur) => {
  if (!tur) return "-";
  const planLabels = {
    "1": "1 Ay",
    "3": "3 Ay",
    "12": "12 Ay",
    "24": "24 Ay",
  };
  return planLabels[tur] || `${tur} Ay`;
};

const AbonelikGecmisiModal = ({ isOpen, onClose, aracData }) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);

  useEffect(() => {
    if (isOpen && aracData?.id) {
      fetchHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, aracData?.id]);

  const fetchHistory = async () => {
    if (!aracData?.id) return;
    setLoading(true);
    try {
      const res = await api.get(`/api/devices/${aracData.id}/abonelik-gecmisi`);
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || "Abonelik geçmişi alınamadı.");
      }
      setRecords(Array.isArray(data) ? data : []);
    } catch (err) {
      setRecords([]);
      toast({
        title: "Hata",
        description: err.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };


  if (!aracData) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered motionPreset="slideInBottom">
      <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="2xl" boxShadow="xl" m={4} maxH="90vh" overflow="hidden" bg="white" p={1}>
        {/* Header */}
        <Box bg="white" p={4} borderBottom="1px solid" borderColor="gray.200">
          <ModalCloseButton size="sm" />
          <VStack spacing={1} align="start">
            <Text fontSize="lg" fontWeight="medium" color="gray.800">
              Abonelik Geçmişi
            </Text>
            <Text fontSize="xs" color="gray.400">
              {aracData?.plaka || "-"} • {aracData?.firma || "-"} • IMEI: {aracData?.imei || "-"}
            </Text>
          </VStack>
        </Box>

        <ModalBody px={6} py={4} maxH="500px" overflowY="auto">
          {loading ? (
            <VStack py={10}>
              <Spinner size="lg" color="blue.500" />
              <Text color="gray.600">Abonelik geçmişi yükleniyor...</Text>
            </VStack>
          ) : records.length === 0 ? (
            <VStack py={10} spacing={3}>
              <Icon as={FiClock} boxSize={10} color="gray.400" />
              <Text color="gray.600">Bu araç için abonelik geçmişi bulunamadı.</Text>
              <Text color="gray.500" fontSize="sm">
                Henüz hiç yenileme/güncelleme yapılmamış olabilir.
              </Text>
            </VStack>
          ) : (
            <VStack align="stretch" spacing={4}>
              <HStack justify="space-between">
                <Text fontSize="sm" color="gray.600">
                  {records.length} kayıt bulundu
                </Text>
                <Button
                  size="sm"
                  variant="outline"
                  leftIcon={<FiRefreshCw />}
                  onClick={fetchHistory}
                  isLoading={loading}
                >
                  Yenile
                </Button>
              </HStack>
              <TableContainer bg="white" borderRadius="xl" shadow="sm">
                <Table size="sm">
                  <Thead bg="gray.100">
                    <Tr>
                      <Th>İşlem Tarihi</Th>
                      <Th>Yenilendiği Tarih</Th>
                      <Th>Eski Bitiş → Yeni Bitiş</Th>
                      <Th>Plan / Döngü</Th>
                      <Th>İşlem</Th>
                      <Th>Açıklama</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {records.map((item) => (
                      <Tr key={item.id} _hover={{ bg: "gray.50" }}>
                        <Td>
                          <VStack align="flex-start" spacing={0}>
                            <Text fontWeight="semibold" fontSize="sm">
                              {formatDate(item.created_at, true)}
                            </Text>
                            <HStack spacing={1} color="gray.500" fontSize="xs">
                              <Icon as={FiUser} />
                              <Text>{item.islem_yapan || "-"}</Text>
                            </HStack>
                          </VStack>
                        </Td>
                        <Td>
                          <VStack align="flex-start" spacing={1}>
                            <Text fontSize="xs" color="gray.600">
                              Eski: {formatDate(item.onceki_yenilendigi_tarih)}
                            </Text>
                            <Text fontSize="xs" color="blue.600" fontWeight="semibold">
                              Yeni: {formatDate(item.yeni_yenilendigi_tarih)}
                            </Text>
                          </VStack>
                        </Td>
                        <Td>
                          <VStack align="flex-start" spacing={1}>
                            <Text fontSize="xs" color="red.500">
                              {formatDate(item.onceki_dongu_tarihi)}
                            </Text>
                            <Text fontSize="xs" color="green.500">
                              {formatDate(item.yeni_dongu_tarihi)}
                            </Text>
                          </VStack>
                        </Td>
                        <Td>
                          <VStack align="flex-start" spacing={1}>
                            <Text fontSize="xs">
                              Plan: {formatPlan(item.onceki_tur)} → {formatPlan(item.yeni_tur)}
                            </Text>
                            <Text fontSize="xs">
                              Döngü: {item.onceki_dongu_sayisi ?? "-"} →{" "}
                              {item.yeni_dongu_sayisi ?? "-"}
                            </Text>
                          </VStack>
                        </Td>
                        <Td>
                          <Badge colorScheme={item.islem_tipi === "yenileme" ? "blue" : "gray"}>
                            {item.islem_tipi === "yenileme" ? "Yenileme" : "Güncelleme"}
                          </Badge>
                        </Td>
                        <Td maxW="220px">
                          <Text fontSize="xs" color="gray.700" noOfLines={3}>
                            {item.aciklama || "-"}
                          </Text>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </VStack>
          )}
        </ModalBody>

        <ModalFooter bg="white" px={6} py={4}>
          <Button variant="ghost" onClick={onClose} size="sm">
            Kapat
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AbonelikGecmisiModal;

